import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BlogCard from "@/components/BlogCard";
import { sightings } from "@/data/sightingsData";
import { useSEO } from "@/hooks/useSEO";

const Blog = () => {
  useSEO({
    title: "Articles & Sighting Reports | Loch Ness Story",
    description: "Read the latest articles, sighting reports, and research about the Loch Ness Monster. In-depth analysis of historical encounters and modern investigations.",
    canonical: "/blog",
    ogImage: "/lovable-uploads/74638626-9ce3-4018-94b3-ec8980da02b9.png",
  });
  const { data: posts, isLoading } = useQuery({
    queryKey: ["blogPosts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select(`*, author:profiles(*)`)
        .order('published_at', { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  // Convert sightings data into the same card format
  const sightingCards = sightings.map((s) => ({
    title: s.title,
    excerpt: s.description.slice(0, 200) + "…",
    image: s.image,
    author: "Loch Ness Story",
    date: s.date,
    categories: s.categories.slice(0, 2),
    slug: `__sighting__${s.id}`, // special prefix to route correctly
    isSighting: true,
    id: s.id,
  }));

  const blogCards = (posts || []).map((post) => ({
    title: post.title,
    excerpt: post.excerpt,
    image: post.image_url || "/placeholder.svg",
    author: post.author?.full_name || post.author?.username || "Anonymous Researcher",
    date: new Date(post.published_at || post.created_at).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'long', year: 'numeric'
    }),
    categories: ["Article"],
    slug: post.slug,
    isSighting: false,
    id: post.id,
  }));

  const allCards = [...blogCards, ...sightingCards];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />
      <main className="flex-grow container mx-auto px-4 py-16">
        <p className="text-accent text-xs font-semibold tracking-[0.3em] uppercase mb-4">Archive</p>
        <h1 className="text-4xl md:text-5xl font-bold mb-4 text-foreground">Articles &amp; Sightings</h1>
        <p className="text-muted-foreground mb-8 max-w-xl">Sightings, research, history, and the people who dedicated their lives to the mystery.</p>
        <div className="h-px bg-border mb-12" />
        {isLoading ? (
          <p className="text-muted-foreground">Loading articles...</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {allCards.map((card) => (
              <SightingAwareBlogCard key={card.id} card={card} />
            ))}
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

// Wrapper that routes sightings to /sightings/:id and blog posts to /blog/:slug
import { Link } from "react-router-dom";

interface CardData {
  title: string;
  excerpt: string;
  image: string;
  author: string;
  date: string;
  categories: string[];
  slug: string;
  isSighting: boolean;
  id: string;
}

const SightingAwareBlogCard = ({ card }: { card: CardData }) => {
  const linkTo = card.isSighting ? `/sightings/${card.id}` : `/blog/${card.slug}`;

  return (
    <Link to={linkTo} className="group">
      <article className="bg-card rounded-xl overflow-hidden border border-border hover:border-accent/30 transition-all duration-300 hover:shadow-lg hover:shadow-accent/5">
        <div className="relative w-full overflow-hidden" style={{ paddingBottom: '56.25%' }}>
          <img
            src={card.image}
            alt={card.title}
            className="absolute inset-0 object-cover w-full h-full group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="p-5">
          <div className="flex gap-2 mb-3">
            {card.categories.map((category) => (
              <span
                key={category}
                className="text-xs font-medium tracking-wide uppercase px-2 py-0.5 bg-secondary text-muted-foreground rounded"
              >
                {category}
              </span>
            ))}
          </div>
          <h3 className="text-lg font-bold mb-2 group-hover:text-accent transition-colors line-clamp-2 text-foreground leading-snug">
            {card.title}
          </h3>
          <p className="text-muted-foreground mb-4 line-clamp-2 text-sm leading-relaxed">{card.excerpt}</p>
          <div className="flex items-center text-xs text-muted-foreground">
            <span>{card.author}</span>
            <span className="mx-2">·</span>
            <span>{card.date}</span>
          </div>
        </div>
      </article>
    </Link>
  );
};

export default Blog;
